<template>
  <v-container class="col-12">
    <v-card class="pa-4">
      <v-row class="mx-0">
        <v-select
          v-model="dateRange"
          :items="dateRangeList"
          label="Period"
          hide-details
          class="col-1"
          style="min-width: 250px"
        ></v-select>
        <date-picker
          v-model="finance.from"
          label="Date From"
          style="max-width: 240px"
          :show-time="true"
          :time-value="finance.fromTime"
          @time-change="finance.fromTime = $event"
        />
        <date-picker
          v-model="finance.to"
          label="Date To"
          style="max-width: 240px"
          :show-time="true"
          :time-value="finance.toTime"
          @time-change="finance.toTime = $event"
        />

        <div class="mx-3" style="font-size: 12px !important;">
          <div class="mb-0"><label class="v-label v-label--active theme--light body-2">Type:</label></div>
          <v-btn-toggle
            v-model="view"
            mandatory
            color="primary"
          >
            <v-btn
              small
              value="table"
            >
              Table
            </v-btn>
            <v-btn
              small
              value="chart"
            >
              Chart
            </v-btn>
          </v-btn-toggle>
        </div>

        <v-btn color="info" @click="search" class="mt-3">Search</v-btn>
        <v-spacer />
        <div class="col-2 pa-0" style="position: relative">
          <total-table-in-select
            v-if="options.totalCalculate && view === 'table'"
            v-model="isTotalExtendOpen"
            :totalTableHeader="totalTableHeader"
            :totalSubGroupByFiltredRows="totalSubGroupByFiltredRows"
            :totalByFiltredRows="totalByFiltredRows"
            :options="options"
          />
        </div>

        <div v-if="view === 'chart'">
          <div class="d-flex">
            <v-checkbox
              v-model="chartParams.forceEur"
              class="mt-0 mr-1 pt-0"
              label="Force EUR"
              hide-details
            />
            <v-checkbox
              v-model="chartParams.includeMonthly"
              class="mt-0 mr-1 pt-0"
              label="Monthly"
              hide-details
            />
            <v-checkbox
              v-model="chartParams.includeWeekly"
              class="mt-0 mr-1 pt-0"
              label="Weekly"
              hide-details
            />
            <v-checkbox
              v-model="chartParams.includeDaily"
              class="mt-0 pt-0"
              label="Daily"
              hide-details
            />
          </div>
          <div class="d-flex justify-end">
            <v-checkbox
              v-model="chartParams.showDepositColumn"
              class="mt-0 mr-1 pt-0"
              label="Show Deposit"
              hide-details
            />
            <v-checkbox
              v-model="chartParams.showWithdrawalColumn"
              class="mt-0 mr-1 pt-0"
              label="Show Withdrawal"
              hide-details
            />
            <v-checkbox
              v-model="chartParams.showBonusColumn"
              class="mt-0 pt-0"
              label="Show Bonus"
              hide-details
            />
          </div>
        </div>
      </v-row>
    </v-card>

    <v-card class="pa-4 mt-4">
      <v-data-table
        v-if="view === 'table'"
        :headers="headers"
        :items="filteredItems"
        sort-by="created"
        sort-desc
        calculate-widths
        dense
        :loading="finance.loading"
        :footer-props="{'items-per-page-options': [15, 25, 50, 100, 250, 500, -1]}"
        :options.sync="finance.tableOptions"
        :server-items-length="finance.totalItems"
        :items-per-page="100"
        class="account-finance-table"
      >
        <template v-slot:item.rollover="{ item }">
          <template v-if="!item.deposit_rollover || !$store.state.auth.checkAccess({key: 'deposit_rollover_update', value: true})">
            {{ item.rollover }}
          </template>
          <template v-else>
            <v-row class="account-finance-deposit_rollover">
              <v-text-field
                v-model="item.deposit_rollover.rollover_up"
                placeholder="Rollover"
                hide-details
                type="number"
                class="mt-0 pt-0 mb-0 pb-0 mr-0 pr-0 pl-0 col-5"
                suffix="/"
              />
              <v-text-field
                v-model="item.deposit_rollover.rollover_init"
                placeholder="Rollover Init"
                hide-details
                type="number"
                class="mt-0 pt-0 mb-0 pb-0 ml-0 pl-0 col-7"
                :append-icon="$store.state.auth.checkAccess({key: 'deposit_rollover_update', value: true}) ? 'mdi-content-save' : ''"
                @click:append="updateRollover(item)"
              />
            </v-row>
          </template>
        </template>
        <template v-slot:item.type="{ item }">
          {{ item.type }}
          <template v-if="item.type == 'chargeback'">({{ item.external_id }})</template>
        </template>
        <template v-slot:item.status="{ item }">
          {{ item.status }}
          <v-icon
            v-if="$store.state.auth.checkAccess({ key: 'payment_return', value: true }) && item.status == 'complete' && item.type == 'deposit' && !item.details.chargeback_id"
            class="float-right"
            style="margin-top:  -3px;"
            @click="openReturn(item)"
          >mdi-cash-refund
          </v-icon>
        </template>
        <template v-slot:item.debit="{value, item}">
          {{ value }}
          <v-tooltip v-if="item.details.express_withdrawal_details" top>
            <template v-slot:activator="{ on, attrs }">
              <v-icon
                color="primary"
                dark
                v-bind="attrs"
                v-on="on"
              >
                help
              </v-icon>
            </template>
            <span>Express withdrawal, fee: {{ item.details.express_withdrawal_details.fee }}</span>
          </v-tooltip>
        </template>

        <template v-slot:item.account_transaction_id="{ item }">

          <div class="nowrap">
            <v-tooltip right>
              <template v-slot:activator="{ on }">
                <span style="cursor: pointer" @click="copyToClipboard(item.alias)" v-on="on">{{ item.account_transaction_id }}</span>
              </template>
              <span>{{ item.alias }}</span>
            </v-tooltip>

            <v-tooltip right v-if="item.details && item.details.benefit">
              <template v-slot:activator="{ on }">
                <v-icon
                  :color="item.status == 'complete' ? 'success' : 'grey'"
                  style="cursor: pointer; font-size: 17px"
                  class="ml-3"
                  dark
                  small
                  v-on="on"
                >mdi-gift-open
                </v-icon>
              </template>
              <span>{{ capitalize(item.details.benefit.type) }}: <br />{{ item.details.benefit.name }} #{{ item.details.benefit.benefit_id }}</span>
            </v-tooltip>
          </div>

        </template>
        <template v-slot:body.prepend>
          <tr class="grey lighten-3 account-finance-table-filter-row">
            <td
              v-for="header in headers"
              :key="header.text"
            >
              <v-select
                v-if="finance.filters.hasOwnProperty(header.value) && finance.filters[header.value].type == 'select'"
                hide-details
                dense
                small
                multiple
                clearable
                persistent-hint
                return-object
                single-line
                class="data-table-finance-filter-select"
                :items="columnValueList(header.value)"
                v-model="finance.filters[header.value].data"
              ></v-select>
              <v-text-field
                class="data-table-finance-filter-textfield"
                v-if="finance.filters.hasOwnProperty(header.value) && finance.filters[header.value].type == 'text'"
                v-model="finance.filters[header.value].data"
                hide-details
              ></v-text-field>
            </td>
          </tr>
        </template>

        <template v-slot:body.append>

          <template v-if="!isSmallScreen">
            <total-calculate-sub
              v-for="row in totalSubGroupByFiltredRows"
              :key="row.id"
              :rowData="row"
              :shadowTotalTitle="true"
              :headers="headers"
              :options="options"
            />
            <total-calculate-rows
              v-if="totalByFiltredRows"
              :row-data="totalByFiltredRows"
              :headers="headers"
              :options="options"
            />
          </template>
        </template>

        <template
          v-if="canViewExtendedTransactionInfo"
          #item.actions="{ item }"
        >
          <v-icon
            v-if="getIsTransactionInfoAvailable(item)"
            @click="selectedTransaction = item, isInspectingTransactionDetails = true"
          >info
          </v-icon>
        </template>
      </v-data-table>
      <template v-if="view === 'chart'">
        <bar-chart
          v-if="chartDataByPeriod.day"
          class="dwb-chart"
          alias="dwb-chart-day"
          title="Deposits, Withdrawals, Bonuses (Day)"
          :data="chartDataByPeriod.day"
          :series="chartDataByPeriod.day.data"
          :options="chartDataByPeriod.day.options"
        />
        <bar-chart
          v-if="chartDataByPeriod.week"
          class="dwb-chart"
          alias="dwb-chart-week"
          title="Deposits, Withdrawals, Bonuses (Week)"
          :data="chartDataByPeriod.week"
          :series="chartDataByPeriod.week.data"
          :options="chartDataByPeriod.week.options"
        />
        <bar-chart
          v-if="chartDataByPeriod.month"
          class="dwb-chart"
          alias="dwb-chart-month"
          title="Deposits, Withdrawals, Bonuses (Month)"
          :data="chartDataByPeriod.month"
          :series="chartDataByPeriod.month.data"
          :options="chartDataByPeriod.month.options"
        />
      </template>
    </v-card>

    <v-dialog
      v-model="returnTr.dialog"
      width="400"
    >
      <v-card v-if="returnTr.item">
        <v-card-title
          class="headline grey lighten-2"
          primary-title
        >
          Return Transaction #{{ returnTr.item.account_transaction_id }}
        </v-card-title>

        <v-card-text class="px-6 pb-0 pt-2">
          <v-row>
            <v-select
              label="Type"
              v-model="returnTr.item.type"
              :items="returnTr.types"
              class="col-6"
            />

            <v-text-field
              label="Amount"
              v-model="returnTr.item.amount"
              :rules="[v => (!!v && v > 0) || 'Amount is required and should be more then 0']"
              :suffix="activeAccount.account.currency"
              class="col-6"
            />
          </v-row>
        </v-card-text>

        <v-divider></v-divider>

        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn
            :loading="finance.loading"
            :disabled="finance.loading"
            color="warning"
            outlined
            @click="returnTr.dialog = false"
          >
            Close
          </v-btn>
          <v-btn
            :loading="finance.loading"
            :disabled="finance.loading"
            color="success"
            outlined
            @click="handleReturn"
          >
            Confirm
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
    <v-dialog
      v-if="canViewExtendedTransactionInfo"
      v-model="isInspectingTransactionDetails"
      max-width="50%"
    >
      <v-card>
        <v-card
          class="finance-detail-table-card"
          v-for="(detailTable, tableIndex) in detailTables" :key="detailTable"
          v-if="selectedTransaction?.details[detailTable]"
        >
          <v-divider v-if="tableIndex > 0" />
          <v-card-title>
            {{ capitalize(detailTable) }}
          </v-card-title>
          <v-divider />
          <v-simple-table
            class="finance-detail-table"
            dense
            hide-default-footer
            fixed-header
          >
            <thead>
              <tr>
                <th>Key</th>
                <th>Value</th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="(value, key) in getFlatObject(selectedTransaction.details[detailTable])"
                :key="key"
              >
                <td>{{ key }}</td>
                <td>{{ value }}</td>
              </tr>
            </tbody>
          </v-simple-table>
        </v-card>
      </v-card>
    </v-dialog>
  </v-container>
</template>
<style>
  .data-table-finance-filter-select .v-select__selections {
    height: 32px;
    overflow: hidden;
    white-space: nowrap;
  }

  .data-table-finance-filter-select {
    max-width: 150px;
    font-size: 12px;
    padding: 0;
    margin: 0;
  }

  .data-table-finance-filter-textfield input {
    text-align: center;
  }

  .data-table-finance-filter-textfield {
    max-width: 80px;
    font-size: 12px;
    padding: 0;
    margin: 0;
  }

  .account-finance-table td:first-child {
    border-left: thin solid rgba(0, 0, 0, 0.12);
  }

  .account-finance-table th {
    background-color: #FFF;
  }

  .account-finance-table th,
  .account-finance-table td {
    padding: 5px 10px !important;
  }

  .account-finance-table td {
    border-right: thin solid rgba(0, 0, 0, 0.12);
  }

  .account-finance-table tr:last-child td {
    border-bottom: thin solid rgba(0, 0, 0, 0.12);
  }

  .account-finance-table .account-finance-table-filter-row td {
    text-align: center;
    border-right: none;
    border-left: none;
  }

  .account-finance-table .account-finance-table-filter-row .v-input {
    margin: 0 auto;
  }

  .nowrap {
    white-space: nowrap;
  }

  .account-finance-deposit_rollover {
    margin-top: -10px;
    margin-bottom: -10px;
    width: 210px;
    padding-left: 10px;
  }

  .account-finance-deposit_rollover .v-text-field {
    zoom: 0.90
  }

  .account-finance-deposit_rollover input {
    text-align: right;
  }

  .finance-detail-table-card:first-child:last-child .finance-detail-table > .v-data-table__wrapper {
    height: 420px;
  }

  .dwb-chart:not(:first-child) {
    margin-top: 0.5rem;
  }
</style>

<script>
  import 'sticky-table-headers'
  import { isEqual, capitalize } from 'lodash'
  import moment from 'moment'
  import { mapGetters } from 'vuex'
  import { API } from '@/utils/api'
  import { totalTableMixin } from '@/components/report/TotalRows/TotalTableMixin'
  import { dataFilterTableMixin } from '@/components/report/Table/dataFilterTableMixin'
  import TotalCalculateRows from '@/components/report/TotalRows/TotalCalculateRows.vue'
  import TotalCalculateSub from '@/components/report/TotalRows/TotalCalculateSub.vue'
  import TotalTableInSelect from '@/components/report/TotalRows/TotalTableInSelect.vue'
  import { isSmallScreenLaptopMixin } from '@/components/report/Table/isSmallScreenLaptopMixin'
  import { filters } from '@/utils/filters'
  import { mask } from 'vue-the-mask'
  import DatePicker from '@/components/core/DatePicker/DatePicker.vue'
  import { makeDateRangeComputed } from '../utils'

  export default {
    directives: {
      mask,
    },
    components: {
      TotalTableInSelect,
      TotalCalculateSub,
      TotalCalculateRows,
      DatePicker,
      ManualTransaction: () => import('@/components/helper/ManualTransaction'),
      BarChart: () => import('./FinanceBarChart'),
    },

    props: [],
    mixins: [ dataFilterTableMixin, totalTableMixin, isSmallScreenLaptopMixin ],
    data: () => ({
      isTotalExtendOpen: false,
      returnTr: {
        dialog: false,
        item: null,
        types: [
          { text: 'Chargeback', value: 'chargeback' },
          { text: 'Refund', value: 'refund' },
        ],
      },
      isInspectingTransactionDetails: false,
      selectedTransaction: null,
      tableOptionAID: null,
      view: null,
      chartParams: {
        includeMonthly: false,
        includeWeekly: false,
        includeDaily: true,
        forceEur: false,
        showBonusColumn: true,
        showDepositColumn: true,
        showWithdrawalColumn: true,
      },
      chartDataByPeriod: {
        day: null,
        week: null,
        month: null,
      },
      dateRangeList,
      options: {
        totalCalculate: {
          fields: [ 'credit', 'debit' ],
          subfields: [ 'credit', 'debit' ],
          titleField: 'account_transaction_id',
          subGroup: 'type'
        },
      },
      headers: [
        { text: 'Created', value: 'created' },
        { text: 'Finished', value: 'completed' },
        { text: 'ID', value: 'account_transaction_id', sortable: true, },
        { text: 'Description', align: 'left', sortable: false, value: 'comment', },
        { text: 'Debit', value: 'debit', sortable: false },
        { text: 'Credit', value: 'credit', sortable: false },
        { text: 'Rollover', value: 'rollover', sortable: false },
        { text: 'Type', value: 'type', sortable: false },
        { text: 'Account', value: 'account', sortable: false },
        { text: 'Status', value: 'status', sortable: false },
        { text: 'By', value: 'operator', sortable: false },
        { text: 'Paymethod', value: 'paymethod', sortable: false },
        { text: 'Paysystem', value: 'paysystem', sortable: false },
      ],
      detailTables: [ 'data', 'cashout', 'state' ]
    }),

    mounted () {
      jQuery('.account-finance-table table').stickyTableHeaders()
      if (this.canViewExtendedTransactionInfo) {
        this.headers.push({ text: '', align: 'center', value: 'actions', sortable: false })
      }
    },

    computed: {
      ...mapGetters({ activeAccount: 'account/active', finance: 'account/finance' }),
      items () {
        return this.finance.items
      },
      filters () {
        return this.finance.filters
      },
      filteredItems: function () {
        return filters(this.finance.items, this.finance.filters)
      },
      canViewExtendedTransactionInfo () {
        return this.$store.state.auth.checkAccess({ key: 'transaction_details', value: true })
      },
      chartColumns () {
        const columns = {
          ykeys: [],
          labels: [],
          colors: []
        }
        if (this.chartParams.showBonusColumn) {
          columns.ykeys.push('bonus')
          columns.labels.push('Bonus')
          columns.colors.push('#36A2EB')
        }
        if (this.chartParams.showDepositColumn) {
          columns.ykeys.push('deposit')
          columns.labels.push('Deposit')
          columns.colors.push('#4CAF50')
        }
        if (this.chartParams.showWithdrawalColumn) {
          columns.ykeys.push('withdraw')
          columns.labels.push('Withdraw')
          columns.colors.push('#CCC')
        }

        return columns
      },

      dateRange: makeDateRangeComputed('finance'),
    },

    watch: {
      'finance.tableOptions': function (newValue, oldValue) {
        if (this.tableOptionAID == this.activeAccount.account_id && !isEqual(newValue, oldValue)) this.search()
        this.tableOptionAID = 1 && this.activeAccount.account_id
      }
    },

    methods: {
      capitalize,
      copyToClipboard: function (text) {

        let el = document.createElement('textarea')
        el.value = text
        el.setAttribute('readonly', '')
        el.style = { position: 'absolute', left: '-9999px' }
        document.body.appendChild(el)
        el.select()
        document.execCommand('copy')
        document.body.removeChild(el)
      },


      openReturn: function (item) {
        this.returnTr.item = {
          account_id: this.activeAccount.account.account_id,
          login: this.activeAccount.account.login,
          type: this.returnTr.types[0].value,
          account_transaction_id: item.account_transaction_id,
          amount: item.amount,
        }
        this.returnTr.dialog = true
      },

      handleReturn: function (item) {
        if (!this.$store.state.auth.checkAccess({ key: 'payment_return', value: true })) {
          return
        }

        this.finance.loading = true
        API.call('/$v/payment_handler/return.json', this.returnTr.item, this.activeAccount.account).then(resp => {
          this.$notification.show = true
          this.$notification.message = capitalize(this.returnTr.item.type) + ' created, check NGR.'
          this.$notification.type = 'success'

          this.returnTr.dialog = false
          this.returnTr.item = null

          this.$store.dispatch('account/load', { value: this.activeAccount.account.login, account_id: this.activeAccount.account.account_id, login: this.activeAccount.account.login }).then(resp => {
            this.$store.commit('account/refresh', resp.data)
          })

          return this.search()
        }).catch(e => {
          this.$notification.show = true
          this.$notification.message = e.response.data.message
          this.$notification.type = 'error'
        }).finally(() => {
          this.finance.loading = false
        })
      },

      updateRollover: async function (item) {
        if (!this.$store.state.auth.checkAccess({ key: 'deposit_rollover_update', value: true })) {
          return
        }
        if (await this.$root.$confirm('Confirm', 'Do you want save changes?')) {
          API.call('/$v/payment_handler/rollover_update.json', {
            account_transaction_id: item.account_transaction_id,
            rollover: item.deposit_rollover.rollover_init - parseFloat(item.deposit_rollover.rollover_up),
            rollover_init: parseFloat(item.deposit_rollover.rollover_init)
          }, this.activeAccount.account).then(resp => {
            return resp.data
          }).catch(e => {
          })
        }
      },

      search: function () {
        if (this.view === 'table') {
          this.$store.dispatch('account/loadFinance', this.activeAccount.account).catch(e => {
            this.$notification.show = true
            this.$notification.message = e.response.data.message
            this.$notification.type = 'error'
          })
        } else {
          this.loadStats()
        }
      },

      loadStats () {
        this.chartDataByPeriod.day = null
        this.chartDataByPeriod.week = null
        this.chartDataByPeriod.month = null
        if (this.chartParams.includeDaily) {
          this.loadStatsByPeriod()
        }
        if (this.chartParams.includeWeekly) {
          this.loadStatsByPeriod('week')
        }
        if (this.chartParams.includeMonthly) {
          this.loadStatsByPeriod('month')
        }
      },

      loadStatsByPeriod (period = 'day') {
        const isEur = this.chartParams.forceEur

        return API.call('/$v/account/finance/stats.json', {
          login: this.activeAccount.account.login,
          from: this.finance.from,
          to: this.finance.to,
          convert2eur: isEur,
          period,
        }).then(response => {

          this.chartDataByPeriod[period] = {
            ...this.chartColumns,
            xkey: 'period',
            series: [],
            options: {},
            data: response.data.map(statsEntry => ({
              bonus: statsEntry[isEur ? 'bonus_deposit_eur' : 'bonus_deposit'] - statsEntry[isEur ? 'bonus_withdraw_eur' : 'bonus_withdraw'],
              deposit: statsEntry[isEur ? 'deposit_eur' : 'deposit'],
              withdraw: statsEntry[isEur ? 'withdraw_eur' : 'withdraw'],
              period: period === 'week' ? this.formatYearWeek(statsEntry[period]) : statsEntry[period],
            }))
          }
        })
      },

      columnValueList (val) {
        return this.finance.items.map(d => d[val]).length == 0 ? this.finance.filters[val].initList : this.finance.items.map(d => d[val])
      },

      getIsTransactionInfoAvailable (item) {
        return item.details.data || item.details.status || item.details.cashout
      },

      getFlatObject (object, prefix = '') {
        const result = {}

        for (const key in object) {
          const prefixedKey = prefix ? `${prefix}.${key}` : key
          if (typeof object[key] === 'object') {
            Object.assign(result, {
              ...this.getFlatObject(object[key], prefixedKey)
            })
          } else {
            result[prefixedKey] = object[key]
          }
        }

        return result
      },

      formatYearWeek (yearweek) {
        const date = moment(yearweek, 'GGGGWW')

        return `${date.weekday(1).format('D-M-Y')} — ${date.weekday(7).format('D-M-Y')}`
      }
    },
  }
</script>
